import { useCallback, useMemo } from 'react'
import { useUserProfile } from 'core/hooks/data/useDataHook'

type RoleTypes =
  | 'isRootAdmin'
  | 'isSuperAdmin'
  | 'isManager'
  | 'isManagerLimited'
  | 'isReviewer'

interface Permissions {
  hasAccessToTeamDashboard: boolean
  canEditMember: boolean
  canRemoveMember: boolean
  canEditTeamSettings: boolean
  canEditTeamBasicSettings: boolean
  canCreateTeam: boolean
  canAssignManager: boolean
  canRemoveManager: boolean
  hasAccessToBilling: boolean
  canRemoveTeam: boolean
  canEditProgram: boolean
  hasAccessToPayPeriods: boolean
  hasFullPower: boolean
  hasAccessToComplianceDashboard: boolean
  canReviewCompliance: boolean
  canPay: boolean
}

interface Roles {
  isRootAdmin: boolean
  isSuperAdmin: boolean
  isManager: boolean
  isManagerLimited: boolean
  isReviewer: boolean
}

interface Response {
  permissions: Permissions
  roles: Roles
  hasOnlyRole: (role: RoleTypes) => boolean
  hasTeam: boolean
  hasPrograms: boolean
  hasOnlyCPM: boolean
}

const useRolesAndPermissions = (): Response => {
  const { data } = useUserProfile()
  const team = data?.teams?.[0]

  const availableRoles = useMemo(() => {
    return team?.roles
      ?.map(({ name }) => name)
      .reduce((accumulator, value: string) => {
        return { ...accumulator, [value.toLocaleLowerCase()]: true }
      }, {}) as Record<string, boolean>
  }, [team?.roles])

  const isRootAdmin = availableRoles?.['account owner'] ?? false
  const isSuperAdmin = availableRoles?.['super admin'] ?? false
  const isManager = availableRoles?.['manager'] ?? false
  const isManagerLimited = availableRoles?.['manager limited'] ?? false
  const isReviewer = availableRoles?.['reviewer'] ?? false

  const permissions: Permissions = useMemo(() => {
    return {
      hasAccessToTeamDashboard: !!data?.teams?.[0]?.roles?.length,
      hasAccessToBilling: isRootAdmin || isSuperAdmin,
      hasAccessToPayPeriods: isRootAdmin || isSuperAdmin,
      hasFullPower: isRootAdmin || isSuperAdmin,
      canEditMember: isRootAdmin || isSuperAdmin || isManager,
      canRemoveMember: isRootAdmin || isSuperAdmin || isManager,
      canEditTeamSettings: isRootAdmin || isSuperAdmin,
      canEditTeamBasicSettings:
        isRootAdmin || isSuperAdmin || isManager || isManagerLimited,
      canCreateTeam: isRootAdmin || isSuperAdmin || isManager,
      canAssignManager: isRootAdmin || isSuperAdmin || isManager,
      canRemoveManager: isRootAdmin || isSuperAdmin || isManager,
      canRemoveTeam: isRootAdmin || isSuperAdmin,
      canEditProgram: isRootAdmin || isSuperAdmin,
      hideContactManager: isRootAdmin || isSuperAdmin || isManager,
      hasAccessToComplianceDashboard:
        isRootAdmin ||
        isSuperAdmin ||
        isManager ||
        isManagerLimited ||
        isReviewer,
      canReviewCompliance: isRootAdmin || isSuperAdmin || isReviewer,
      canPay: isRootAdmin || isSuperAdmin
    }
  }, [
    data?.teams,
    isManager,
    isManagerLimited,
    isReviewer,
    isRootAdmin,
    isSuperAdmin
  ])

  const roles: Roles = useMemo(() => {
    return {
      isRootAdmin,
      isSuperAdmin,
      isManager,
      isManagerLimited,
      isReviewer
    }
  }, [isManager, isManagerLimited, isReviewer, isRootAdmin, isSuperAdmin])

  const hasOnlyRole = useCallback(
    (role: RoleTypes) => {
      if (roles[role] === undefined) return false
      const tempRoles = { ...roles }
      delete tempRoles[role]
      const hasOtherRoles = Object.values(tempRoles).some((value) => value)
      return roles[role] && !hasOtherRoles
    },
    [roles]
  )

  return {
    permissions,
    roles,
    hasOnlyRole,
    hasTeam: !!team?.token_id,
    hasPrograms: !!team?.has_programs,
    hasOnlyCPM: !!team?.has_cpm_programs_only
  }
}

export default useRolesAndPermissions
